import { Button, Cut } from '@core/ui';
import { Duration } from 'luxon';
import { ChangeEvent, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseTimelineEvent, Timeline } from '../../../parser';
import { ActiveTrack } from '../../atoms';
import { ChaptersTrack, ChatTrack, CurrentTimeIndicator, PollsTrack, SlidesTrack, TimeTrack, VideoLayoutTrack, VideoTrim } from '../../molecules';
import { AddItemButton } from '../../molecules/AddItemButton';

export const TimelineOrganism = ({
    timeline,
    currentTime,
    duration,
    activeTrack,
    onSeek,
    onAdd,
    onSelectTrack
}: {
    timeline: Timeline;
    currentTime: Duration;
    duration: Duration;
    activeTrack: ActiveTrack;
    onSeek: (seek: Duration) => void;
    onAdd: (track: ActiveTrack) => void;
    onSelectTrack: (track: ActiveTrack, event?: BaseTimelineEvent) => void;
}) => {
    const { t } = useTranslation();

    const trackContainerRef = useRef<HTMLDivElement>(null);
    const maxTimelineWidth = duration.seconds * 10 + 1;
    const [minTimelineWidth, setMinTimelineWidth] = useState(501);
    const [timelineWidth, setTimelineWidth] = useState(minTimelineWidth);

    const timelineZoomChange = (e: ChangeEvent<HTMLInputElement>) => {
        setTimelineWidth(Number(e.target.value));
    };

    useLayoutEffect(() => {
        if (!trackContainerRef.current) {
            return;
        }

        const onResize = () => {
            if (!trackContainerRef.current) {
                return;
            }

            const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
            const containerWidth = trackContainerRef.current.getBoundingClientRect().width - 4 * fontSize;
            const minWidth = Math.max(containerWidth, 501);
            setMinTimelineWidth(minWidth);
            if (timelineWidth < minWidth) {
                setTimelineWidth(minWidth);
            }
        };

        onResize();
        globalThis.window.addEventListener('resize', onResize);
        return () => globalThis.window.removeEventListener('resize', onResize);
    }, [trackContainerRef.current, timelineWidth]);

    return (
        <div className="col-span-full row-start-2 flex">
            <div className="grid grid-rows-(--v-edit-grid-rows) gap-y-0.5 pb-2 track-names">
                <div>
                    {currentTime.toFormat('hh:mm:ss')}
                    <Button variant="warning-outline" iconbtn classNames="ms-3" onClick={() => onAdd(ActiveTrack.VideoTrim)}>
                        <span className="sr-only">Trim video</span>
                        <Cut classNames="size-6 stroke-current" />
                    </Button>
                </div>
                <div className={activeTrack === ActiveTrack.VideoLayout ? 'active' : ''} onClick={() => onSelectTrack(ActiveTrack.VideoLayout)}>
                    {t('Editor.Trackname.VideoLayout')}
                    {/*<AddItemButton onClick={() => onAdd(ActiveTrack.VideoLayout)}>{t('Editor.VideoLayout.Button.Add')}</AddItemButton>*/}
                </div>
                <div className={activeTrack === ActiveTrack.Slide ? 'active' : ''} onClick={() => onSelectTrack(ActiveTrack.Slide)}>
                    {t('Editor.Trackname.Slides')}
                    {/*<AddItemButton onClick={() => onAdd(ActiveTrack.Slide)}>{t('Editor.Slides.Button.Add')}</AddItemButton>*/}
                </div>
                <div className={activeTrack === ActiveTrack.Chapter ? 'active' : ''} onClick={() => onSelectTrack(ActiveTrack.Chapter)}>
                    {t('Editor.Trackname.Chapters')}
                    <AddItemButton onClick={() => onAdd(ActiveTrack.Chapter)}>{t('Editor.Chapter.Button.Add')}</AddItemButton>
                </div>
                <div className={activeTrack === ActiveTrack.Poll ? 'active' : ''} onClick={() => onSelectTrack(ActiveTrack.Poll)}>
                    {t('Editor.Trackname.Polls')}
                </div>
                <div className={activeTrack === ActiveTrack.Chat ? 'active' : ''} onClick={() => onSelectTrack(ActiveTrack.Chat)}>
                    {t('Editor.Trackname.Chat')}
                    {/*<AddItemButton onClick={() => onAdd(ActiveTrack.Chat)}>{t('Editor.Chat.Button.Add')}</AddItemButton>*/}
                </div>
            </div>
            <div className="min-w-0 flex-1" ref={trackContainerRef}>
                <div className="overflow-x-scroll pb-2">
                    <div className="wrapper-inner box-content px-8" style={{ width: timelineWidth }}>
                        <div className="relative grid grid-rows-(--v-edit-grid-rows) gap-y-0.5">
                            <CurrentTimeIndicator currentTime={currentTime} duration={duration} onSeek={onSeek} />
                            <TimeTrack duration={duration} onSeek={onSeek} timelineWidth={timelineWidth} />
                            <VideoLayoutTrack
                                timeline={timeline}
                                duration={duration}
                                isActive={activeTrack === ActiveTrack.VideoLayout}
                                onClick={(event) => onSelectTrack(ActiveTrack.VideoLayout, event)}
                            />
                            <SlidesTrack
                                timeline={timeline}
                                duration={duration}
                                isActive={activeTrack === ActiveTrack.Slide}
                                onClick={(event) => onSelectTrack(ActiveTrack.Slide, event)}
                            />
                            <ChaptersTrack
                                timeline={timeline}
                                duration={duration}
                                isActive={activeTrack === ActiveTrack.Chapter}
                                onClick={(event) => onSelectTrack(ActiveTrack.Chapter, event)}
                            />
                            <PollsTrack
                                timeline={timeline}
                                duration={duration}
                                isActive={activeTrack === ActiveTrack.Poll}
                                onClick={(event) => onSelectTrack(ActiveTrack.Poll, event)}
                            />
                            <ChatTrack
                                timeline={timeline}
                                duration={duration}
                                isActive={activeTrack === ActiveTrack.Chat}
                                onClick={(event) => onSelectTrack(ActiveTrack.Chat, event)}
                            />
                            <VideoTrim timeline={timeline} duration={duration} onClick={(event) => onSelectTrack(ActiveTrack.VideoTrim, event)} />
                        </div>
                    </div>
                </div>
                <label className="flex gap-2 py-2">
                    {t('Editor.Timeline.Zoom.Text')}
                    <input type="range" min={minTimelineWidth} max={maxTimelineWidth} step="10" defaultValue={minTimelineWidth} onChange={timelineZoomChange} />
                </label>
            </div>
        </div>
    );
};
